import { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { removeProvinceFromArea, addProvinceToArea, updateArea } from '../services/project/areaService';
import SearchableDropdown from './SearchableDropdown';
import { useUsers } from '../context/UserContext';

const AreaModal = ({ projectId, regionId, area, show, onClose, onAreaMutation, provinces }) => {
  const [areaName, setAreaName] = useState(area?.name ?? '');
  const [headcount, setHeadcount] = useState(area?.headcount ?? 0);
  const [dailyQuota, setDailyQuota] = useState(area?.dailyQuota ?? 0);
  const [selectedProvinces, setSelectedProvinces] = useState((area?.provinces || []).map(p => ({ value: p.id, label: p.name })));
  const [savingName, setSavingName] = useState(false);

  const { isSuperAdmin } = useUsers();

  useEffect(() => {
    setAreaName(area?.name ?? '');
    setHeadcount(area?.headcount ?? 0);
    setDailyQuota(area?.dailyQuota ?? 0);
    setSelectedProvinces((area?.provinces || []).map(p => ({ value: p.id, label: p.name })));
  }, [area?.id]);

  const handleProvinceChange = (action, provinceId) => {
    if (action === 'add') {
      setSelectedProvinces(prev => [...prev, { value: provinceId, label: provinces.find(p => p.id === provinceId)?.name }]);
    } else if (action === 'remove') {
      setSelectedProvinces(prev => prev.filter(p => p.value !== provinceId));
    }
  };

  const handleSaveName = async () => {
    const newName = areaName.trim();
    if (!newName) {
      toast.error('Area name is required');
      return;
    }
    
    const currentProvinceIds = selectedProvinces.map(p => p.value).sort();
    const originalProvinceIds = (area?.provinces || []).map(p => p.id).sort();
    const provincesChanged = JSON.stringify(currentProvinceIds) !== JSON.stringify(originalProvinceIds);
    
    if (newName === (area?.name ?? '') && headcount === (area?.headcount ?? 0) && dailyQuota === (area?.dailyQuota ?? 0) && !provincesChanged) {
      toast.info('No changes to save');
      return;
    }
    
    try {
      setSavingName(true);
      
      // Update area name and headcount
      const response = await updateArea(projectId, regionId, area.id, { name: newName, headcount, dailyQuota });
      if (!response?.success) {
        toast.error(response?.data?.error || 'Failed to update area');
        return;
      }
      
      // Handle province changes
      if (provincesChanged) {
        const provincesToAdd = currentProvinceIds.filter(id => !originalProvinceIds.includes(id));
        const provincesToRemove = originalProvinceIds.filter(id => !currentProvinceIds.includes(id));
        
        // Add new provinces
        for (const provinceId of provincesToAdd) {
          const addResponse = await addProvinceToArea(projectId, regionId, area.id, provinceId);
          if (!addResponse.success) {
            toast.error(addResponse.data?.error || 'Failed to add province');
            return;
          }
        }
        
        // Remove provinces
        for (const provinceId of provincesToRemove) {
          const removeResponse = await removeProvinceFromArea(projectId, area.id, provinceId);
          if (!removeResponse.success) {
            toast.error(removeResponse.data?.error || 'Failed to remove province');
            return;
          }
        }
      }
      
      toast.success(response.message || 'Area updated');
      onAreaMutation?.();
      onClose();
    } catch (error) {
      console.error('Error updating area:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    } finally {
      setSavingName(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} fullscreen="md-down" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Area</Modal.Title>
      </Modal.Header>

      <Modal.Body
        className="d-flex flex-column"
        style={{ height: '100%', maxHeight: '80vh', overflow: 'hidden' }}
      >
        <Form className="w-100">
          {/* Area name */}
          { isSuperAdmin && 
          (  <Form.Group className="mb-3">
              <Form.Label>Area Name</Form.Label>
              <Form.Control
                type="text"
                value={areaName}
                onChange={e => setAreaName(e.target.value)}
                placeholder="Enter area name"
              />
            </Form.Group>)
           }

          {/* Headcount */}
          <Form.Group className="mb-3">
            <Form.Label>Headcount</Form.Label>
            <Form.Control
              type="number"
              value={headcount}
              onChange={e => setHeadcount(parseInt(e.target.value) || 0)}
              placeholder="Enter headcount"
              min="0"
            />
          </Form.Group>

          {/* Daily Quota */}
          <Form.Group className="mb-3">
            <Form.Label>Daily Quota</Form.Label>
            <Form.Control
              type="number"
              value={dailyQuota}
              onChange={e => setDailyQuota(parseInt(e.target.value) || 0)}
              placeholder="Enter daily quota"
              min="0"
            />
          </Form.Group>

          {/* Provinces multi-select */}
          <SearchableDropdown
            key={area?.id}
            name="provinceIds"
            label="Select Provinces"
            options={(provinces || []).map(p => ({ value: p.id, label: p.name }))}
            defaultValue={selectedProvinces}
            onSelect={(provinceId) => handleProvinceChange('add', provinceId)}
            onDelete={(provinceId) => handleProvinceChange('remove', provinceId)}
            isMulti
          />
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveName}
          disabled={savingName}
        >
          {savingName ? <Spinner size="sm" /> : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AreaModal;
